import styled from "@emotion/styled";
import type { ReactNode } from "react";

export type TableColumnAlign = "left" | "right" | "center";

export interface TableColumn<T> {
  id: keyof T & string;
  label: string;
  align?: TableColumnAlign;
  format?: (value: unknown, row: T) => ReactNode;
}

export interface TableProps<T> {
  readonly columns: TableColumn<T>[];
  readonly data: T[];
  readonly "aria-label"?: string;
  readonly className?: string;
  readonly rowKey?: keyof T | ((row: T, index: number) => string | number);
}

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(props) => props.theme.typography.body4Regular.fontFamily};
  font-size: ${(props) => props.theme.typography.body4Regular.fontSize};
  line-height: ${(props) => props.theme.typography.body4Regular.lineHeight};
`;

const StyledThead = styled.thead`
  border-bottom: 1px solid ${(props) => props.theme.colors.text.mediumLowEmphasis};
`;

const Th = styled.th<{ align?: TableColumnAlign }>`
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  text-align: ${(props) => props.align ?? "left"};
  color: ${(props) => props.theme.colors.text.mediumLowEmphasis};
  font-weight: ${(props) => props.theme.typography.body4Bold.fontWeight};
`;

const StyledTbody = styled.tbody``;

const Td = styled.td<{ align?: TableColumnAlign }>`
  padding: ${(props) => props.theme.spacing(1.5)} ${(props) => props.theme.spacing(2)};
  text-align: ${(props) => props.align ?? "left"};
  color: ${(props) => props.theme.colors.text.mediumEmphasis};
  border-bottom: 1px solid ${(props) => props.theme.colors.text.disabled};
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border-radius: ${(props) => props.theme.shape.borderRadiusLarge};
`;

function getCellContent<T>(col: TableColumn<T>, row: T): ReactNode {
  const value = row[col.id as keyof T];
  if (col.format) return col.format(value, row);
  return value == null ? "—" : String(value);
}

export function Table<T>({
  columns,
  data,
  "aria-label": ariaLabel,
  className,
  rowKey,
}: TableProps<T>) {
  return (
    <TableWrapper className={className}>
      <StyledTable aria-label={ariaLabel}>
        <StyledThead>
          <tr>
            {columns.map((col) => (
              <Th key={String(col.id)} align={col.align}>
                {col.label}
              </Th>
            ))}
          </tr>
        </StyledThead>
        <StyledTbody>
          {data.map((row, rowIndex) => {
            let key: string;
            if (rowKey === undefined) {
              key = `row-${rowIndex}`;
            } else if (typeof rowKey === "function") {
              key = String(rowKey(row, rowIndex));
            } else {
              key = String(row[rowKey]);
            }
            return (
              <tr key={key}>
                {columns.map((col) => (
                  <Td key={String(col.id)} align={col.align}>
                    {getCellContent(col, row)}
                  </Td>
                ))}
              </tr>
            );
          })}
        </StyledTbody>
      </StyledTable>
    </TableWrapper>
  );
}
